import React from "react";
import { useTranslation } from "react-i18next";
import { useColors } from "../../constants/Colors";
import StepWrapper from "./StepWrapper";
import {
  CreateFlatStore,
  MAX_ROOM_RENT,
  MAX_UTILITIES,
  MAX_DEPOSIT,
} from "../../hooks/useCreateFlatStore";

interface Props {
  store: CreateFlatStore;
}

const PricingStep: React.FC<Props> = ({ store }) => {
  const { t } = useTranslation();
  const {
    textNeutralDefault,
    textNeutralMuted,
    accentColor,
    containerNeutralLightMedium,
  } = useColors();

  const currency = store.getCurrency();

  const handleNumberInput = (
    value: string,
    setter: (val: string) => void,
    max: number,
  ) => {
    const cleaned = value.replace(/[^0-9]/g, "");
    if (cleaned === "" || parseInt(cleaned) <= max) {
      setter(cleaned);
    }
  };

  const totalMonthly =
    parseInt(store.rent || "0") + parseInt(store.utilities || "0");

  return (
    <StepWrapper
      title={t("flats_tab.add_flat.flat_price_title") || "What's the price?"}
    >
      <div className="space-y-1">
        {/* Subsidised toggle */}
        <div className="flex items-center justify-between py-4">
          <span className="font-medium" style={{ color: textNeutralDefault }}>
            {t("flats_tab.add_flat.flat_subsidized") || "Subsidised housing"}
          </span>
          <button
            onClick={() => store.setIsSubsidised(!store.isSubsidised)}
            className="cf-toggle"
            style={{
              backgroundColor: store.isSubsidised
                ? accentColor
                : containerNeutralLightMedium,
            }}
          >
            <span
              className="cf-toggle-knob"
              data-on={store.isSubsidised ? "true" : "false"}
            />
          </button>
        </div>

        {/* Rent */}
        <div
          className="flex items-center justify-between py-4"
          style={{ borderBottom: "1px solid rgba(2, 2, 16, 0.06)" }}
        >
          <span style={{ color: textNeutralMuted }}>
            {t("flats_tab.add_flat.flat_rent") || "Monthly rent"}
          </span>
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              value={store.rent}
              onChange={(e) =>
                handleNumberInput(e.target.value, store.setRent, MAX_ROOM_RENT)
              }
              placeholder="0"
              className="cf-price-input"
              style={{ color: textNeutralDefault }}
            />
            <span
              className="text-sm font-medium w-10"
              style={{ color: textNeutralMuted }}
            >
              {currency}
            </span>
          </div>
        </div>

        {/* Utilities */}
        <div
          className="flex items-center justify-between py-4"
          style={{ borderBottom: "1px solid rgba(2, 2, 16, 0.06)" }}
        >
          <span style={{ color: textNeutralMuted }}>
            {t("flats_tab.add_flat.flat_utilities") || "Utilities"}
          </span>
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              value={store.utilities}
              onChange={(e) =>
                handleNumberInput(
                  e.target.value,
                  store.setUtilities,
                  MAX_UTILITIES,
                )
              }
              placeholder="0"
              className="cf-price-input"
              style={{ color: textNeutralDefault }}
            />
            <span
              className="text-sm font-medium w-10"
              style={{ color: textNeutralMuted }}
            >
              {currency}
            </span>
          </div>
        </div>

        {/* Deposit */}
        <div className="flex items-center justify-between py-4">
          <span style={{ color: textNeutralMuted }}>
            {t("flats_tab.add_flat.flat_deposit") || "Deposit"}
          </span>
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              value={store.deposit}
              onChange={(e) =>
                handleNumberInput(e.target.value, store.setDeposit, MAX_DEPOSIT)
              }
              placeholder="0"
              className="cf-price-input"
              style={{ color: textNeutralDefault }}
            />
            <span
              className="text-sm font-medium w-10"
              style={{ color: textNeutralMuted }}
            >
              {currency}
            </span>
          </div>
        </div>

        {/* Total preview — hidden on mobile */}
        <div
          className="mt-4 p-5 rounded-2xl hidden sm:block"
          style={{
            background: `linear-gradient(135deg, rgba(91, 61, 246, 0.07) 0%, rgba(91, 61, 246, 0.03) 100%)`,
          }}
        >
          <p className="text-xs tracking-wide uppercase font-medium mb-1" style={{ color: textNeutralMuted }}>
            {t("total_monthly") || "Total monthly"}
          </p>
          <p
            className="text-3xl font-gnarly-semibold tracking-tight"
            style={{ color: textNeutralDefault }}
          >
            {currency} {totalMonthly.toLocaleString()}
          </p>
        </div>
      </div>
    </StepWrapper>
  );
};

export default PricingStep;
